"use client";

import { Button } from "@/components/shadcn/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/shadcn/dialog";
import { Slider } from "@/components/shadcn/slider";
import { Spinner } from "@/components/shadcn/spinner";
import { Switch } from "@/components/shadcn/switch";
import type { ColorChoice, CreateRoomResponse } from "@satranc365/shared";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useActiveGame } from "../_hooks/useActiveGame";
import { useChessSocketContext } from "../_providers/ChessSocketProvider";

// Valid base times in seconds following spec step rules
const VALID_BASE_TIMES_SECONDS = [
    15,
    30,
    45,
    60, // 15s steps up to 1min
    90,
    120, // 30s steps up to 2min
    ...Array.from({ length: 18 }, (_, i) => (i + 3) * 60), // 1min steps: 3-20 min
    ...Array.from({ length: 5 }, (_, i) => (i * 5 + 25) * 60), // 5min steps: 25-45 min
    ...Array.from({ length: 9 }, (_, i) => (i * 15 + 60) * 60), // 15min steps: 60-180 min
];

// Valid increments in seconds following spec step rules
const VALID_INCREMENTS_SECONDS = [
    ...Array.from({ length: 21 }, (_, i) => i), // 0-20 in 1s steps
    25,
    30,
    35,
    40,
    45, // 5s steps
    60,
    90,
    120,
    150,
    180, // specific values
];

// Preset time controls (base in seconds, increment in seconds)
const TIME_CONTROL_PRESETS = [
    { base: 60, inc: 0, label: "1+0" },
    { base: 120, inc: 1, label: "2+1" },
    { base: 180, inc: 0, label: "3+0" },
    { base: 180, inc: 2, label: "3+2" },
    { base: 300, inc: 0, label: "5+0" },
    { base: 300, inc: 3, label: "5+3" },
    { base: 600, inc: 0, label: "10+0" },
    { base: 600, inc: 5, label: "10+5" },
    { base: 900, inc: 10, label: "15+10" },
    { base: 1800, inc: 0, label: "30+0" },
    { base: 1800, inc: 20, label: "30+20" },
];

function getBaseTimeDisplayMinutes(seconds: number): string {
    if (seconds < 60) {
        return `${seconds}sn`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
        return `${minutes}`;
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

interface ColorOption {
    value: ColorChoice;
    label: string;
    icon: React.ReactNode;
}

const COLOR_OPTIONS: ColorOption[] = [
    {
        value: "w",
        label: "Beyaz",
        icon: (
            <div className="border-border h-6 w-6 rounded-full border-2 bg-white" />
        ),
    },
    {
        value: "random",
        label: "Rastgele",
        icon: (
            <div className="border-border relative h-6 w-6 overflow-hidden rounded-full border-2">
                <div className="absolute top-0 left-0 h-full w-1/2 bg-white" />
                <div className="absolute top-0 right-0 h-full w-1/2 bg-zinc-800" />
            </div>
        ),
    },
    {
        value: "b",
        label: "Siyah",
        icon: (
            <div className="border-border h-6 w-6 rounded-full border-2 bg-zinc-800" />
        ),
    },
];

// Default to 5+0 (5 minutes, 0 increment)
const DEFAULT_BASE_TIME_INDEX = VALID_BASE_TIMES_SECONDS.indexOf(300);
const DEFAULT_INCREMENT_INDEX = 0;

interface CreateGameDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    inviteTarget?: string;
}

export function CreateGameDialog({
    open,
    onOpenChange,
    inviteTarget,
}: CreateGameDialogProps) {
    const t = useTranslations("game");
    const router = useRouter();
    const { socket, isConnected } = useChessSocketContext();
    const [isCreating, setIsPending] = useState(false);
    const [selectedColor, setSelectedColor] = useState<ColorChoice>("random");
    const [baseTimeIndex, setBaseTimeIndex] = useState(DEFAULT_BASE_TIME_INDEX);
    const [incrementIndex, setIncrementIndex] = useState(
        DEFAULT_INCREMENT_INDEX,
    );
    const [watchable, setWatchable] = useState(false);
    const [analysis, setAnalysis] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [existingRoomCode, setExistingRoomCode] = useState<string | null>(
        null,
    );
    const [activeGameFromResponse, setActiveGameFromResponse] = useState<
        string | null
    >(null);
    const activeGameFromHook = useActiveGame();
    const activeGameRoomCode = activeGameFromHook ?? activeGameFromResponse;

    const baseTimeSeconds = VALID_BASE_TIMES_SECONDS[baseTimeIndex];
    const incrementSeconds = VALID_INCREMENTS_SECONDS[incrementIndex];

    const canCreate = isConnected && !isCreating;

    const activePresetIndex = useMemo(() => {
        return TIME_CONTROL_PRESETS.findIndex(
            (p) => p.base === baseTimeSeconds && p.inc === incrementSeconds,
        );
    }, [baseTimeSeconds, incrementSeconds]);

    const handlePresetClick = useCallback(
        (preset: (typeof TIME_CONTROL_PRESETS)[0]) => {
            const baseIndex = VALID_BASE_TIMES_SECONDS.indexOf(preset.base);
            const incIndex = VALID_INCREMENTS_SECONDS.indexOf(preset.inc);
            if (baseIndex !== -1) setBaseTimeIndex(baseIndex);
            if (incIndex !== -1) setIncrementIndex(incIndex);
        },
        [],
    );

    const createRoom = useCallback(
        async (
            baseTimeMs: number,
            incrementMs: number,
            hostColor: ColorChoice,
        ): Promise<CreateRoomResponse> => {
            if (!socket || !isConnected) {
                throw new Error("Socket not connected");
            }

            setIsPending(true);

            try {
                return await socket.emitWithAck(
                    "create_room",
                    { initial: baseTimeMs, increment: incrementMs },
                    hostColor,
                    { watchable, analysis: watchable && analysis },
                );
            } finally {
                setIsPending(false);
            }
        },
        [socket, isConnected, watchable, analysis],
    );

    const handleSuccess = useCallback(
        (roomCode: string) => {
            if (inviteTarget && socket) {
                socket.emit(
                    "send_game_invite",
                    inviteTarget,
                    (inviteResponse) => {
                        if (inviteResponse.success) {
                            router.push(
                                `/oyun/${roomCode}?invited=${encodeURIComponent(inviteTarget)}`,
                            );
                        } else {
                            toast.error(
                                inviteResponse.error || t("inviteFailed"),
                            );
                            router.push(`/oyun/${roomCode}`);
                        }
                        onOpenChange(false);
                    },
                );
            } else {
                router.push(`/oyun/${roomCode}`);
                onOpenChange(false);
            }
        },
        [inviteTarget, socket, router, onOpenChange],
    );

    const handleCreate = useCallback(async () => {
        if (!canCreate) return;

        setError(null);
        setExistingRoomCode(null);
        setActiveGameFromResponse(null);

        try {
            const baseTimeMs = baseTimeSeconds * 1000;
            const incrementMs = incrementSeconds * 1000;

            const response = await createRoom(
                baseTimeMs,
                incrementMs,
                selectedColor,
            );

            if (!response.success) {
                if (response.existingRoomCode) {
                    setExistingRoomCode(response.existingRoomCode);
                    return;
                }
                if (response.activeGameRoomCode) {
                    setActiveGameFromResponse(response.activeGameRoomCode);
                    return;
                }
                setError(response.error || t("gameCreateFailed"));
                return;
            }

            if (response.roomCode) {
                handleSuccess(response.roomCode);
            }
        } catch (err) {
            setError(
                err instanceof Error ? err.message : t("gameCreateFailed"),
            );
        }
    }, [
        canCreate,
        baseTimeSeconds,
        incrementSeconds,
        selectedColor,
        createRoom,
        handleSuccess,
    ]);

    const handleCancelExistingAndRetry = useCallback(async () => {
        if (!socket || isCreating) return;
        setError(null);
        setIsPending(true);
        let leaveResponse;
        try {
            leaveResponse = await socket.emitWithAck("leave_room");
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : "Mevcut oda iptal edilemedi",
            );
            return;
        } finally {
            setIsPending(false);
        }
        if (!leaveResponse.success) {
            toast.error(leaveResponse.error || "Mevcut oda iptal edilemedi");
            return;
        }
        setExistingRoomCode(null);
        // Retry create with the same selections
        await handleCreate();
    }, [socket, isCreating, handleCreate]);

    const handleResignAndRetry = useCallback(async () => {
        if (!socket || isCreating) return;
        setError(null);
        setIsPending(true);
        let resignResponse;
        try {
            resignResponse = await socket.emitWithAck("resign");
        } catch (err) {
            toast.error(err instanceof Error ? err.message : t("resignFailed"));
            return;
        } finally {
            setIsPending(false);
        }
        if (!resignResponse.success) {
            toast.error(resignResponse.error || t("resignFailed"));
            return;
        }
        // Server emits game_over → useActiveGame clears activeGameFromHook.
        // Also clear the response-driven copy so the prompt dismisses, then
        // retry create with the same selections.
        setActiveGameFromResponse(null);
        await handleCreate();
    }, [socket, isCreating, handleCreate]);

    const handleGoToExisting = useCallback(() => {
        if (!existingRoomCode) return;
        router.push(`/oyun/${existingRoomCode}`);
        onOpenChange(false);
    }, [existingRoomCode, router, onOpenChange]);

    const handleOpenChange = (isOpen: boolean) => {
        // Prevent closing the dialog while game is being created
        if (!isOpen && isCreating) {
            return;
        }
        onOpenChange(isOpen);
        if (!isOpen) {
            setError(null);
            setExistingRoomCode(null);
            setActiveGameFromResponse(null);
        }
    };

    // If create_room reported the user already has an open waiting room,
    // offer to either go to it or cancel it and create a new one.
    if (existingRoomCode) {
        return (
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t("openRoomTitle")}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-center">
                        <p className="text-muted-foreground text-sm">
                            {t("openRoomBody")}
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button
                                onClick={handleGoToExisting}
                                className="w-full"
                            >
                                Mevcut Odaya Git
                            </Button>
                            <Button
                                onClick={handleCancelExistingAndRetry}
                                disabled={isCreating}
                                variant="outline"
                                className="w-full"
                            >
                                {isCreating ? (
                                    <>
                                        <Spinner data-icon="inline-start" />
                                        {t("processing")}
                                    </>
                                ) : (
                                    t("cancelOldAndCreate")
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // If user has an active game, offer to either rejoin it or resign and
    // create a new one.
    if (activeGameRoomCode) {
        return (
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Aktif Oyun</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-center">
                        <p className="text-muted-foreground text-sm">
                            {t("inGameBody")}
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button
                                onClick={() => {
                                    router.push(`/oyun/${activeGameRoomCode}`);
                                    onOpenChange(false);
                                }}
                                className="w-full"
                            >
                                Oyuna Git
                            </Button>
                            <Button
                                onClick={handleResignAndRetry}
                                disabled={isCreating}
                                variant="outline"
                                className="w-full"
                            >
                                {isCreating ? (
                                    <>
                                        <Spinner data-icon="inline-start" />
                                        {t("processing")}
                                    </>
                                ) : (
                                    t("resignAndCreate")
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {inviteTarget
                            ? t("createInviteTitle")
                            : t("createGameTitle")}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Color Selection */}
                    <div className="space-y-3">
                        <label className="text-sm font-medium">
                            {t("colorChoice")}
                        </label>
                        <div className="flex justify-center gap-2">
                            {COLOR_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    aria-label={option.label}
                                    onClick={() =>
                                        setSelectedColor(option.value)
                                    }
                                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-3 transition-colors ${
                                        selectedColor === option.value
                                            ? "border-primary bg-primary/10"
                                            : "border-border hover:border-muted-foreground/50"
                                    }`}
                                >
                                    {option.icon}
                                    <span className="text-xs font-medium">
                                        {option.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Time Control Selection */}
                    <div className="space-y-4">
                        {/* Labels and Display */}
                        <div className="flex items-center justify-between">
                            <span className="text-muted-foreground text-sm">
                                {t("timePerSide")}
                            </span>
                            <div className="bg-muted rounded-md px-3 py-1.5 text-center">
                                <span className="text-lg font-semibold">
                                    {getBaseTimeDisplayMinutes(baseTimeSeconds)}
                                </span>
                                <span className="text-muted-foreground text-lg font-semibold">
                                    {" + "}
                                </span>
                                <span className="text-lg font-semibold">
                                    {incrementSeconds}
                                </span>
                            </div>
                            <span className="text-muted-foreground text-sm">
                                {t("increment")}
                            </span>
                        </div>

                        {/* Sliders */}
                        <div className="flex gap-4">
                            <Slider
                                value={[baseTimeIndex]}
                                onValueChange={(value) =>
                                    setBaseTimeIndex(value[0])
                                }
                                min={0}
                                max={VALID_BASE_TIMES_SECONDS.length - 1}
                                step={1}
                                className="flex-1"
                            />
                            <Slider
                                value={[incrementIndex]}
                                onValueChange={(value) =>
                                    setIncrementIndex(value[0])
                                }
                                min={0}
                                max={VALID_INCREMENTS_SECONDS.length - 1}
                                step={1}
                                className="flex-1"
                            />
                        </div>

                        {/* Preset Buttons */}
                        <div className="flex flex-wrap justify-center gap-1.5">
                            {TIME_CONTROL_PRESETS.map((preset, index) => (
                                <button
                                    key={preset.label}
                                    type="button"
                                    onClick={() => handlePresetClick(preset)}
                                    className={`rounded-md px-2.5 py-1 text-sm font-medium transition-colors ${
                                        activePresetIndex === index
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-muted hover:bg-muted/80"
                                    }`}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Spectating opt-in — matches are private by default */}
                    <div className="space-y-3">
                        <label className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                                {t("watchableLabel")}
                            </span>
                            <Switch
                                checked={watchable}
                                onCheckedChange={(checked) => {
                                    setWatchable(checked);
                                    if (!checked) setAnalysis(false);
                                }}
                                data-testid="watchable-switch"
                            />
                        </label>
                        {watchable && (
                            <label className="flex items-center justify-between gap-2">
                                <span className="text-sm">
                                    {t("analysisLabel")}
                                </span>
                                <Switch
                                    checked={analysis}
                                    onCheckedChange={setAnalysis}
                                    data-testid="analysis-switch"
                                />
                            </label>
                        )}
                        <p className="text-muted-foreground text-xs">
                            {t("watchableHint")}
                        </p>
                    </div>

                    {/* Connection Status */}
                    {!isConnected && (
                        <p className="text-muted-foreground text-center text-sm">
                            {t("notConnected")}
                        </p>
                    )}

                    {/* Error Message */}
                    {error && (
                        <p className="text-destructive text-center text-sm">
                            {error}
                        </p>
                    )}

                    {/* Create Button */}
                    <Button
                        onClick={handleCreate}
                        disabled={!canCreate}
                        className="w-full"
                    >
                        {isCreating ? (
                            <>
                                <Spinner data-icon="inline-start" />
                                {t("creating")}
                            </>
                        ) : inviteTarget ? (
                            t("createAndInvite")
                        ) : (
                            t("createGame")
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
